import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";

import VisibilityIcon from "@mui/icons-material/Visibility";
import ImageIcon from "@mui/icons-material/Image";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import LogoutButton from "layouts/logout";

import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "core/interceptors/api";
import { ServerBaseUrlForStaticImages } from "constants";

const columns = [
  { Header: "S.No.", accessor: "sno" },
  { Header: "Business Name", accessor: "business_name" },
  { Header: "Owner Name", accessor: "owner_name" },
  { Header: "Mobile", accessor: "mobile_number" },
  { Header: "Options", accessor: "options" },
];

function VerifyVendors() {
  const navigate = useNavigate();

  const [vendors, setVendors] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openImageModal, setOpenImageModal] = useState(false);
  const [openProfileModal, setOpenProfileModal] = useState(false);

  const [selectedVendor, setSelectedVendor] = useState(null);
  const [checkedImages, setCheckedImages] = useState({});
  const [allChecked, setAllChecked] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState(null);

  const imageKeys = ["vendor_photo", "shop_photo", "sgo_gps_photo"];

  useEffect(() => {
    fetchUnverifiedVendors();
  }, []);

  const fetchUnverifiedVendors = async () => {
    try {
      const res = await api.get("/vendor_register/fetch_unverified");
      setVendors(res.data.vendors || []);
      setPage(0);
    } catch {
      Swal.fire("Error", "Failed to load vendors", "error");
    }
  };

  /* ---------------- IMAGE CHECK LOGIC ---------------- */

  const proofDocs = Array.isArray(selectedVendor?.user_proof_doc)
    ? selectedVendor.user_proof_doc
    : [];

  const allKeys = [
    ...imageKeys.filter((k) => selectedVendor?.[k]),
    ...proofDocs.map((_, i) => `proof-${i}`),
  ];

  useEffect(() => {
    const init = {};
    allKeys.forEach((k) => (init[k] = false));
    setCheckedImages(init);
  }, [selectedVendor]);

  useEffect(() => {
    setAllChecked(allKeys.length > 0 && allKeys.every((k) => checkedImages[k]));
  }, [checkedImages, allKeys]);

  /* ---------------- VERIFY ---------------- */

  const handleVerify = async () => {
    const confirm = await Swal.fire({
      title: "Verify Vendor?",
      text: "All images are verified. Continue?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Verify",
    });

    if (!confirm.isConfirmed) return;

    try {
      await api.put(`/vendor_register/verify_vendor/${selectedVendor.id}`);
      Swal.fire("Success", "Vendor verified successfully", "success");
      setOpenImageModal(false);
      setSelectedVendor(null);
      fetchUnverifiedVendors();
    } catch {
      Swal.fire("Error", "Verification failed", "error");
    }
  };

  /* ---------------- REJECT ---------------- */

  const handleReject = async () => {
    if (!selectedVendor) return;

    const result = await Swal.fire({
      title: "Reject Vendor?",
      text: "Please enter rejection reason",
      input: "text",
      inputLabel: "Reason",
      inputValidator: (value) => {
        if (!value) return "Reason is required!";
      },
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Reject",
    });

    if (!result.isConfirmed) return;

    try {
      await api.put(
        `/vendor_register/reject_vendor_reason/${selectedVendor.id}`,
        { reason: result.value }
      );
      await api.post(`/vendor_register/reject_vendor/${selectedVendor.id}`);

      Swal.fire("Rejected", "Vendor rejected successfully", "success");

      setOpenImageModal(false);
      setSelectedVendor(null);
      setCheckedImages({});
      fetchUnverifiedVendors();
    } catch {
      Swal.fire("Error", "Failed to reject vendor", "error");
    }
  };

  /* ---------------- SEARCH + PAGINATION ---------------- */

  const filtered = vendors.filter((v) => {
    const q = searchQuery.toLowerCase();
    return (
      v.business_name.toLowerCase().includes(q) ||
      v.owner_name.toLowerCase().includes(q) ||
      v.mobile_number.includes(q)
    );
  });

  const start = page * rowsPerPage;
  const paginated = filtered.slice(start, start + rowsPerPage);

  const rows = paginated.map((v, i) => ({
    sno: start + i + 1,
    business_name: v.business_name,
    owner_name: v.owner_name,
    mobile_number: v.mobile_number,
    options: (
      <>
        <IconButton
          color="info"
          onClick={() => {
            setSelectedVendor(v);
            setOpenProfileModal(true);
          }}
        >
          <VisibilityIcon />
        </IconButton>

        <IconButton
          color="success"
          onClick={() => {
            setSelectedVendor(v);
            setOpenImageModal(true);
          }}
        >
          <ImageIcon />
        </IconButton>
      </>
    ),
  }));

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end">
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>

      <DashboardNavbar />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg">
                <MDTypography color="white">Verify Vendors</MDTypography>
              </MDBox>

              <MDBox p={2}>
                <TextField
                  fullWidth
                  label="Search"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                />
              </MDBox>

              <MDBox p={2}>
                <DataTable
                  table={{ columns, rows }}
                  entriesPerPage={{ defaultValue: rowsPerPage }}
                  pagination={{
                    page,
                    rowsPerPage,
                    count: filtered.length,
                    onPageChange: (_, p) => setPage(p),
                    onRowsPerPageChange: (_, s) => {
                      setRowsPerPage(s);
                      setPage(0);
                    },
                  }}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ---------------- IMAGE VERIFY MODAL ---------------- */}
      <Dialog open={openImageModal} onClose={() => setOpenImageModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Verify Vendor Images</DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            {imageKeys.map(
              (key) =>
                selectedVendor?.[key] && (
                  <Grid item xs={4} key={key}>
                    <img
                      src={`${ServerBaseUrlForStaticImages}vendor_register/media/${selectedVendor[key]}`}
                      style={{ width: "100%", cursor: "pointer" }}
                      onClick={() =>
                        setFullScreenImage(
                          `${ServerBaseUrlForStaticImages}vendor_register/media/${selectedVendor[key]}`
                        )
                      }
                    />
                    <Checkbox
                      checked={checkedImages[key] || false}
                      onChange={(e) =>
                        setCheckedImages((p) => ({ ...p, [key]: e.target.checked }))
                      }
                    />
                  </Grid>
                )
            )}

            {proofDocs.map((img, i) => {
              const k = `proof-${i}`;
              return (
                <Grid item xs={4} key={k}>
                  <img
                    src={`${ServerBaseUrlForStaticImages}vendor_register/media/${img}`}
                    style={{ width: "100%", cursor: "pointer" }}
                    onClick={() =>
                      setFullScreenImage(
                        `${ServerBaseUrlForStaticImages}vendor_register/media/${img}`
                      )
                    }
                  />
                  <Checkbox
                    checked={checkedImages[k] || false}
                    onChange={(e) =>
                      setCheckedImages((p) => ({ ...p, [k]: e.target.checked }))
                    }
                  />
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
          <MDButton onClick={() => setOpenImageModal(false)}>Close</MDButton>

          <Box display="flex" gap={2}>
            <MDButton color="error" startIcon={<CancelIcon />} onClick={handleReject}>
              Reject
            </MDButton>

            <MDButton
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleVerify}
              disabled={!allChecked}
            >
              Verify
            </MDButton>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ---------------- FULL IMAGE ---------------- */}
      <Dialog open={Boolean(fullScreenImage)} onClose={() => setFullScreenImage(null)} maxWidth="xl">
        <DialogContent>
          <img src={fullScreenImage} style={{ width: "100%" }} />
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default VerifyVendors;
